import React, { useState } from "react";
import "./compatibility-info.scss";
import WhiteArrow from "../../../assets/Icons/WhiteArrow";

const CompatibilityInfo = () => {
  const [isOpen, setIsOpen] = useState(false);

  return (
    <section className="compatibility-info-section">
      <div className="compatibility-info-wrapper">
        {/* Header */}
        <div className="compatibility-header">
          <h4 className="compatibility-heading">Compatibility Info</h4>
          <button
            className={`toggle-btn ${isOpen ? "open" : "closed"}`}
            onClick={() => setIsOpen(!isOpen)}
          >
            <WhiteArrow />
          </button>
        </div>

        {/* Body */}
        {isOpen && (
          <>
            <p className="compatibility-subtext">
              <strong>Last Updated: 01 July 2025</strong>
            </p>

            <h3>📱 Devices Compatible with eSIM</h3>

            {/* iPhone */}
            <div className="info-block">
              <h5>🍏 iPhones with eSIM</h5>
              <p>
                💡 <strong>On iPhone 13 and newer</strong>, you can have <strong>two eSIMs activated simultaneously.</strong>
              </p>

              <p><strong>Supported iPhone models:</strong></p>
              <ul>
                <li>iPhone XR</li>
                <li>iPhone XS / XS Max</li>
                <li>iPhone 11 / 11 Pro / 11 Pro Max</li>
                <li>iPhone SE 2 (2020)</li>
                <li>iPhone 12 / Mini / Pro / Pro Max</li>
                <li>iPhone 13 / Mini / Pro / Pro Max</li>
                <li>iPhone SE 3 (2022)</li>
                <li>iPhone 14 / Plus / Pro / Pro Max</li>
                <li>iPhone 15 / Plus / Pro / Pro Max</li>
                <li>iPhone 16 / Plus / Pro / Pro Max / 16e</li>
                <li>iPhone 17 / Pro / Pro Max</li>
                <li>iPhone Air</li>
              </ul>

              <p><strong>Important Notes:</strong></p>
              <ul>
                <li>
                  iPhones from Mainland China and most Hong Kong/Macao units don’t support eSIM
                  (except: 13 Mini, 12 Mini, SE 2020, XS).
                </li>
                <li>In the USA, iPhone 14 and newer (non-SE) are <strong>eSIM-only</strong>.</li>
                <li>
                  iPhone 17 series is:
                  <ul>
                    <li>SIM-only in Mainland China</li>
                    <li>
                      eSIM-only in Bahrain, Canada, Guam, Japan, Kuwait, Mexico, Oman,
                      Qatar, Saudi Arabia, UAE, USA
                    </li>
                  </ul>
                </li>
                <li>iPhone Air is Apple’s first globally eSIM-only model.</li>
              </ul>
            </div>

            {/* iPads */}
            <div className="info-block">
              <h5>🍏 iPads with eSIM</h5>
              <p>(Only cellular-capable iPads support eSIM)</p>
              <ul>
                <li>iPad Pro 11″ (A2068, 2020)</li>
                <li>iPad Pro 12.9″ (A2069, 2020)</li>
                <li>iPad Air (A2123, 2019)</li>
                <li>iPad (A2198, 2019)</li>
                <li>iPad Mini (A2124, 2019)</li>
                <li>iPad 10th Gen (2022)</li>
              </ul>
            </div>

            {/* Samsung */}
            <div className="info-block">
              <h5>📱 Samsung Phones with eSIM</h5>

              <p><strong>Galaxy S Series:</strong></p>
              <ul>
                <li>S20 / S20+ / S20 Ultra (+5G)</li>
                <li>S21 / S21+ / S21 Ultra</li>
                <li>S22 / S22+ / S22 Ultra</li>
                <li>S23 / S23+ / S23 Ultra / FE*</li>
                <li>S24 / S24+ / S24 Ultra / FE</li>
                <li>S25 / S25+ / S25 Ultra / S25 Edge</li>
              </ul>

              <p><strong>Galaxy Note:</strong></p>
              <ul>
                <li>Note 20 / Note 20 Ultra 5G</li>
              </ul>

              <p><strong>Galaxy Foldables:</strong></p>
              <ul>
                <li>Z Fold 1 → Fold7</li>
                <li>Z Flip → Flip7 / Flip7 FE</li>
              </ul>

              <p><strong>Galaxy A Series:</strong></p>
              <ul>
                <li>A54 / A55 / A56 / A35 / A36</li>
              </ul>

              <p><strong>Not Compatible:</strong></p>
              <ul>
                <li>Galaxy S20 FE 4G/5G</li>
                <li>US Galaxy S20/S21, Note20 Ultra, Z Fold2, Z Flip 5G</li>
                <li>All Samsung Hong Kong models (except Flip SM-F700F)</li>
                <li>Certain Korea variants of S20/S21/S22/Note20/Fold/Flip</li>
              </ul>
            </div>

            {/* Google Pixel */}
            <div className="info-block">
              <h5>📱 Google Pixel Phones with eSIM</h5>
              <ul>
                <li>Pixel 2 / 2 XL (Fi only)</li>
                <li>Pixel 3 → Pixel 10 Pro XL</li>
                <li>Pixel Fold</li>
              </ul>

              <p><strong>Not Compatible:</strong></p>
              <ul>
                <li>Pixel 3 (AU, JP, TW)</li>
                <li>Pixel 3a (SEA, Verizon)</li>
                <li>All Hong Kong Pixel models</li>
              </ul>
            </div>

            {/* Huawei */}
            <div className="info-block">
              <h5>📱 Huawei Phones with eSIM</h5>
              <ul>
                <li>P40 / P40 Pro</li>
                <li>Mate 40 Pro</li>
                <li>Pura 70 Pro</li>
              </ul>
              <p><strong>Not Compatible:</strong> P40 Pro+, P50 Pro</p>
            </div>

            {/* Oppo */}
            <div className="info-block">
              <h5>📱 Oppo Phones with eSIM</h5>
              <ul>
                <li>Find X3 / X3 Pro</li>
                <li>Find X5 / X5 Pro / X8 / X8 Pro</li>
                <li>Find N2 Flip / N3 / N3 Flip</li>
                <li>Reno 5A / 6 Pro 5G / 9A / 14 / 14 Pro</li>
                <li>Oppo A55s 5G</li>
              </ul>

              <p><strong>Not Compatible:</strong> Oppo Lite series</p>
            </div>

            {/* Sony */}
            <div className="info-block">
              <h5>📱 Sony Phones with eSIM</h5>
              <ul>
                <li>Xperia 10 III Lite / 10 IV / 10 V</li>
                <li>Xperia 1 IV / 1 V / 1 VI</li>
                <li>Xperia 5 IV / 5 V</li>
                <li>Xperia Ace III</li>
              </ul>
            </div>

            {/* Xiaomi */}
            <div className="info-block">
              <h5>📱 Xiaomi Phones with eSIM</h5>
              <ul>
                <li>Xiaomi 12T Pro</li>
                <li>13 / 13 Lite / 13 Pro / 13T / 13T Pro</li>
                <li>14 / 14 Pro / 14T / 14T Pro</li>
                <li>Redmi Note 13 Pro+ / Note 14 Pro / Pro+</li>
                <li>Poco X7</li>
                <li>Xiaomi 15 / 15 Ultra</li>
              </ul>
            </div>

            {/* Motorola */}
            <div className="info-block">
              <h5>📱 Motorola Phones with eSIM</h5>
              <ul>
                <li>Razr series (2019 → 2024)</li>
                <li>Edge series (2022 → 2024)</li>
                <li>Moto G Power / G52J / G53J / G54 / G84 / G34 / G35 / G Stylus 5G</li>
              </ul>
            </div>

            {/* Sharp */}
            <div className="info-block">
              <h5>📱 Sharp Phones with eSIM</h5>
              <ul>
                <li>AQUOS sense4 / sense6s / sense7 / sense8</li>
                <li>AQUOS Wish / Wish2 / Wish3</li>
                <li>AQUOS zero 6</li>
                <li>AQUOS R7 / R8 / R8 Pro</li>
                <li>Simple Sumaho6</li>
              </ul>
            </div>

            {/* Rakuten */}
            <div className="info-block">
              <h5>📱 Rakuten Phones with eSIM</h5>
              <ul>
                <li>Rakuten Mini</li>
                <li>Rakuten Big / Big-S</li>
                <li>Rakuten Hand / Hand 5G</li>
              </ul>
            </div>

            {/* Honor */}
            <div className="info-block">
              <h5>📱 Honor Phones with eSIM</h5>
              <ul>
                <li>Honor Magic 4 Pro / 5 Pro / 6 Pro / V2 / V3</li>
                <li>Honor 90 / 200 Pro / 400 Lite / X8</li>
              </ul>
            </div>

            {/* Vivo */}
            <div className="info-block">
              <h5>📱 Vivo Phones with eSIM</h5>
              <ul>
                <li>Vivo X80 Pro → X200 Pro</li>
                <li>Vivo V29 / V40 / V40 SE</li>
              </ul>
            </div>

            {/* Other */}
            <div className="info-block">
              <h5>📱 Other eSIM-Compatible Devices</h5>
              <ul>
                <li>Gemini PDA</li>
                <li>Fairphone 4 / 5</li>
                <li>DOOGEE V30</li>
                <li>OnePlus Open / 11 / 12 / 13 series</li>
                <li>HAMMER Blade series</li>
                <li>Nokia XR21 / X30 / G60</li>
                <li>OUKITEL WP30 Pro / WP33 Pro</li>
                <li>ZTE Nubia Flip</li>
                <li>Realme 14 Pro+ / GT 7</li>
                <li>Nothing Phone 3 / 3a Pro</li>
              </ul>
            </div>
          </>
        )}
      </div>
    </section>
  );
};

export default CompatibilityInfo;
